import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Newspaper, ArrowSquareOut, CircleNotch, Sparkle, Warning, MapPin } from "@phosphor-icons/react";

const GS_TABS = [
    { key: "ALL", label: "All" },
    { key: "GS1", label: "GS1 · Society/History/Geo" },
    { key: "GS2", label: "GS2 · Polity/IR" },
    { key: "GS3", label: "GS3 · Economy/Env/S&T" },
    { key: "GS4", label: "GS4 · Ethics" },
];

const GS_COLOR = {
    GS1: { chip: "bg-rose-50 text-rose-700 border-rose-200", accent: "text-rose-700" },
    GS2: { chip: "bg-indigo-50 text-indigo-700 border-indigo-200", accent: "text-indigo-700" },
    GS3: { chip: "bg-emerald-50 text-emerald-700 border-emerald-200", accent: "text-emerald-700" },
    GS4: { chip: "bg-amber-50 text-amber-800 border-amber-200", accent: "text-amber-800" },
};

export default function DailyArticles() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState("ALL");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/current-affairs/articles");
                setData(data);
            } catch (e) {
                setError(e.response?.data?.detail || "Could not load today's articles");
            }
        })();
    }, []);

    const filtered = useMemo(() => {
        if (!data?.articles) return [];
        if (tab === "ALL") return data.articles;
        return data.articles.filter((a) => (a.gs_paper || "").toUpperCase() === tab);
    }, [data, tab]);

    const counts = useMemo(() => {
        const c = { GS1: 0, GS2: 0, GS3: 0, GS4: 0 };
        (data?.articles || []).forEach((a) => {
            const k = (a.gs_paper || "").toUpperCase();
            if (c[k] !== undefined) c[k] += 1;
        });
        return c;
    }, [data]);

    if (error) {
        return (
            <Layout>
                <div className="max-w-3xl mx-auto mt-8 border-2 border-red-200 bg-red-50/60 rounded-2xl p-8 text-center">
                    <Warning size={40} weight="duotone" className="text-red-600 mx-auto mb-3" />
                    <div className="font-heading font-bold text-xl text-red-900">Couldn&apos;t load today&apos;s articles</div>
                    <p className="text-sm text-red-700 mt-2">{error}</p>
                </div>
            </Layout>
        );
    }

    if (!data) {
        return (
            <Layout>
                <div className="min-h-[50vh] grid place-items-center text-slate-500 gap-3">
                    <CircleNotch size={36} className="animate-spin text-[#1E3A8A]" />
                    <div className="text-sm">Curating today&apos;s UPSC-relevant articles from The Hindu & The New Indian Express…</div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-400">First load of the day may take 60-120 seconds</div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            {/* Header */}
            <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 rounded-full border-2 border-emerald-200 bg-emerald-50/70 px-3 py-1 text-xs font-semibold text-emerald-800 mb-3">
                    <Sparkle size={14} weight="fill" /> Daily UPSC Feed · {data.display_date}
                </div>
                <h1 className="font-heading font-black text-3xl sm:text-4xl">Today&apos;s Articles</h1>
                <p className="text-slate-500 mt-2 text-sm max-w-2xl mx-auto">
                    India & Karnataka-connected snippets curated from <span className="font-semibold text-slate-700">The Hindu</span> and <span className="font-semibold text-slate-700">The New Indian Express</span>, mapped to UPSC General Studies papers.
                </p>
            </div>

            {/* GS Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
                {GS_TABS.map((t) => {
                    const active = tab === t.key;
                    const count = t.key === "ALL" ? (data.articles?.length || 0) : (counts[t.key] || 0);
                    return (
                        <button
                            key={t.key}
                            data-testid={`gs-tab-${t.key}`}
                            onClick={() => setTab(t.key)}
                            className={`px-4 py-2 rounded-full border-2 text-xs font-semibold transition-colors ${
                                active
                                    ? "bg-[#1E3A8A] text-white border-[#1E3A8A]"
                                    : "bg-white text-slate-700 border-[#BFD3FF] hover:border-[#1E3A8A]"
                            }`}
                        >
                            {t.label}
                            <span className={`ml-2 inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[10px] ${active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"}`}>
                                {count}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Articles list */}
            {filtered.length === 0 ? (
                <div className="max-w-3xl mx-auto text-center text-slate-500 py-16">
                    <Newspaper size={40} weight="duotone" className="mx-auto text-slate-400 mb-3" />
                    <div>No articles in this section today.</div>
                </div>
            ) : (
                <div className="grid gap-4 max-w-4xl mx-auto">
                    {filtered.map((a) => {
                        const gs = (a.gs_paper || "GS2").toUpperCase();
                        const color = GS_COLOR[gs] || GS_COLOR.GS2;
                        const scopeLabel = a.scope === "Karnataka" ? "Karnataka" : a.scope === "Both" ? "India + Karnataka" : "India";
                        return (
                            <article
                                key={a.article_id}
                                data-testid={`daily-article-${a.article_id}`}
                                className="bento-card !p-6 hover:border-[#1E3A8A]/40 transition-colors"
                            >
                                <div className="flex flex-wrap items-center gap-2 mb-3">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider ${color.chip}`}>
                                        {gs}
                                    </span>
                                    {a.theme && (
                                        <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                                            {a.theme}
                                        </span>
                                    )}
                                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                                        <MapPin size={12} weight="duotone" /> {scopeLabel}
                                    </span>
                                    <span className="ml-auto text-[11px] text-slate-400">
                                        {a.source_paper}{a.source_section ? ` · ${a.source_section}` : ""}
                                    </span>
                                </div>

                                <h3 className="font-heading font-bold text-lg text-slate-900 leading-snug mb-2">
                                    {a.title || a.original_title}
                                </h3>

                                {a.why_in_news && (
                                    <div className="mb-2">
                                        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mr-2">Why in news</span>
                                        <span className="text-sm text-slate-700">{a.why_in_news}</span>
                                    </div>
                                )}

                                <p className="text-sm text-slate-700 leading-relaxed">{a.snippet}</p>

                                {Array.isArray(a.keywords) && a.keywords.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-4">
                                        {a.keywords.slice(0, 8).map((kw, i) => (
                                            <span key={i} className="text-[10px] font-medium bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md border border-slate-200">
                                                {kw}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {a.source_url && (
                                    <div className="mt-4 pt-3 border-t border-[#BFD3FF]/60">
                                        <a
                                            href={a.source_url}
                                            target="_blank"
                                            rel="noreferrer noopener"
                                            data-testid={`source-link-${a.article_id}`}
                                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1E3A8A] hover:underline"
                                        >
                                            Read full article at source <ArrowSquareOut size={13} weight="bold" />
                                        </a>
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            )}
        </Layout>
    );
}
